/**
 * ResponsiveDialog - Bottom sheet on mobile, centered dialog on desktop.
 *
 * Use this for non-form dialogs (sharing, info panels, pickers, etc.).
 * For form dialogs with submit/cancel buttons, use DialogForm instead.
 */
import { type ReactNode } from 'react';
import { cn } from '../../../lib/utils';
import { useResponsive } from '../../hooks/useResponsive';
// eslint-disable-next-line no-restricted-imports -- ResponsiveDialog IS a shared wrapper around raw Dialog
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../../components/ui/sheet';

interface ResponsiveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Dialog title */
  title: ReactNode;
  children: ReactNode;
  /** Additional className for desktop DialogContent (e.g. 'max-w-md') */
  className?: string;
}

export function ResponsiveDialog({
  open,
  onOpenChange,
  title,
  children,
  className,
}: ResponsiveDialogProps) {
  const { isMobile } = useResponsive();

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          className="flex max-h-[85dvh] flex-col rounded-t-xl p-0"
          hideCloseButton
          aria-describedby={undefined}
        >
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-1">
            <div className="h-1 w-10 rounded-full bg-muted-foreground/30" />
          </div>
          <SheetHeader className="px-4 pb-3">
            <SheetTitle className="text-start">{title}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-4 pb-4">
            {children}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn('max-h-[90vh] flex flex-col', className)}
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto min-h-0">
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
}
